import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { getBreadcrumbs } from '@plone/volto/actions/breadcrumbs/breadcrumbs';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';

// design system home glyph — see CONTRACT E in PORT-SPEC.md / _ds_bundle_home.js
// (components/chrome/Breadcrumb.jsx, `const HOME`)
const HOME_SVG = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 11.5 12 4l9 7.5" />
    <path d="M5.5 10v9.5h13V10" />
  </svg>
);

const controlpanelItems = [
  {
    url: '/controlpanel',
    title: 'Site Setup',
  },
];

/**
 * Shadows @plone/volto/components/theme/Breadcrumbs/Breadcrumbs.
 * Same data wiring as core (getBreadcrumbs dispatch, state.breadcrumbs
 * items/root, hasApiExpander/getBaseUrl guards) — only the markup is
 * restructured to emit the design's `.prc-breadcrumb` DOM (CONTRACT E).
 */
const BreadcrumbsComponent = ({ pathname }) => {
  const dispatch = useDispatch();
  const { pathname: realPath } = useLocation();

  const items = useSelector(
    (state) =>
      realPath.startsWith('/controlpanel')
        ? controlpanelItems
        : state.breadcrumbs.items,
    shallowEqual,
  );
  const root = useSelector((state) => state.breadcrumbs.root);

  useEffect(() => {
    if (!hasApiExpander('breadcrumbs', getBaseUrl(pathname))) {
      dispatch(getBreadcrumbs(getBaseUrl(pathname)));
    }
  }, [dispatch, pathname]);

  return (
    <nav className="prc-breadcrumb" aria-label="Breadcrumbs">
      <ol className="prc-breadcrumb__inner">
        <li>
          <Link to={root || '/'} aria-label="Home" className="prc-breadcrumb__home">
            {HOME_SVG}
          </Link>
        </li>
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={item.url}>
              <span className="prc-breadcrumb__sep" aria-hidden="true">
                /
              </span>
              {last ? (
                <span className="prc-breadcrumb__current" aria-current="page">
                  {item.title}
                </span>
              ) : (
                <Link to={item.url}>{item.title}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

BreadcrumbsComponent.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default BreadcrumbsComponent;
